// ScreenTour.js — lista de recursos + moldura: escolher um item troca a captura mostrada.
// Padrão de abas (WAI-ARIA): setas movem entre os itens, a captura é o painel.
import React, { useId, useRef, useState } from 'react';
import { PhoneFrame, BrowserFrame } from './Frames';

function ScreenTour({ items, frame = 'phone', label, reverse = false }) {
  const [active, setActive] = useState(0);
  // Só monta a imagem de um item depois que ele foi aberto: nada de baixar oito capturas de uma vez.
  const [seen, setSeen] = useState(() => new Set([0]));
  const tabs = useRef([]);
  const uid = useId();
  const isPhone = frame === 'phone';
  const current = items[active];

  const select = (index, focus = false) => {
    setActive(index);
    setSeen((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
    if (focus && tabs.current[index]) tabs.current[index].focus();
  };

  const onKeyDown = (event) => {
    const next = isPhone ? ['ArrowDown', 'ArrowRight'] : ['ArrowRight', 'ArrowDown'];
    const prev = isPhone ? ['ArrowUp', 'ArrowLeft'] : ['ArrowLeft', 'ArrowUp'];
    let target = null;
    if (next.includes(event.key)) target = (active + 1) % items.length;
    else if (prev.includes(event.key)) target = (active - 1 + items.length) % items.length;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = items.length - 1;
    if (target === null) return;
    event.preventDefault();
    select(target, true);
  };

  const slides = items.map((item, index) =>
    seen.has(index) ? (
      <img
        key={item.id}
        className={`pid-tour-slide ${index === active ? 'is-active' : ''}`}
        src={item.img}
        alt={index === active ? item.alt : ''}
        aria-hidden={index === active ? undefined : true}
        width={isPhone ? 600 : 1600}
        height={isPhone ? 1298 : 1000}
        loading="lazy"
        decoding="async"
      />
    ) : null
  );

  return (
    <div className={`pid-tour pid-tour--${frame} ${reverse ? 'is-reverse' : ''}`}>
      <div className="pid-tour-nav">
        <div
          className="pid-tour-tabs"
          role="tablist"
          aria-label={label}
          aria-orientation={isPhone ? 'vertical' : 'horizontal'}
          onKeyDown={onKeyDown}
        >
          {items.map((item, index) => (
            <button
              key={item.id}
              ref={(el) => {
                tabs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`${uid}-tab-${item.id}`}
              aria-selected={index === active}
              aria-controls={`${uid}-panel`}
              tabIndex={index === active ? 0 : -1}
              className={`pid-tour-tab ${index === active ? 'is-active' : ''}`}
              onClick={() => select(index)}
            >
              <span className="pid-tour-tab-n" aria-hidden>
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="pid-tour-tab-body">
                <span className="pid-tour-tab-title">{item.title}</span>
                {isPhone && <span className="pid-tour-tab-desc">{item.desc}</span>}
              </span>
            </button>
          ))}
        </div>
        {!isPhone && <p className="pid-tour-caption">{current.desc}</p>}
      </div>

      <div
        className="pid-tour-stage"
        role="tabpanel"
        id={`${uid}-panel`}
        aria-labelledby={`${uid}-tab-${current.id}`}
      >
        <div className="pid-tour-glow" aria-hidden />
        {isPhone ? (
          <PhoneFrame>{slides}</PhoneFrame>
        ) : (
          <BrowserFrame url={current.url}>{slides}</BrowserFrame>
        )}
        {/* No celular a descrição fica sob a moldura: a lista vira uma faixa de chips. */}
        {isPhone && <p className="pid-tour-caption pid-tour-caption--mobile">{current.desc}</p>}
      </div>
    </div>
  );
}

export default ScreenTour;
